import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { inr, fmtDateTime, LEAD_STATUSES, MEETING_STATUSES, LOST_REASONS, SOURCES } from '../lib/format';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { PhoneCall, Save, Trash2, Loader2, Flame } from 'lucide-react';
import { CommActions } from './CommActions';
import { LeadMeetingsTab } from './meetings/LeadMeetingsTab';
import { emitLeadChanged } from '../lib/events';
import { toast } from 'sonner';
import logger from '../lib/logger';

const NONE = '__none__';
const ROLE_OPTIONS = ['Sales Head', 'Sales Executive', 'Sales Intern'];
const SALE_TYPE_OPTIONS = ['1-Call Sale', 'Follow-Up Sale'];

const toLocalInput = (iso) => (iso ? String(iso).slice(0, 16) : '');
const toDateInput = (iso) => (iso ? String(iso).slice(0, 10) : '');

const Field = ({ label, children }) => (
  <div className="space-y-1">
    <Label className="text-xs text-muted-foreground">{label}</Label>
    {children}
  </div>
);

// Reusable "nullable select" — collapses the repeated Select/NONE pattern
const SelectField = ({ label, value, onChange, options, placeholder, testId, triggerClass }) => (
  <Field label={label}>
    <Select value={value || NONE} onValueChange={(v) => onChange(v === NONE ? null : v)}>
      <SelectTrigger data-testid={testId} className={triggerClass}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE}>—</SelectItem>
        {options.map((o) => (
          <SelectItem key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  </Field>
);

// Payload field groups — keeps the payload builder declarative and flat
const PASSTHROUGH_FIELDS = ['lead_name', 'company_name', 'email', 'phone', 'category', 'city',
  'source', 'setter_id', 'closer_id', 'team', 'assigned_role', 'status', 'date_created', 'remarks'];
const NULLABLE_FIELDS = ['next_followup_date', 'first_contact_at', 'date_meeting_booked',
  'date_of_meeting', 'meeting_status', 'sale_type', 'lost_reason', 'deposit_date', 'date_paid_in_full'];
const NUMERIC_FIELDS = ['deposit_amount', 'total_deal_value', 'cash_collected', 'refund_amount',
  'commission_pct', 'total_contract_value', 'contract_period_months', 'monthly_retainer'];

const buildLeadPayload = (form) => ({
  ...Object.fromEntries(PASSTHROUGH_FIELDS.map((k) => [k, form[k]])),
  ...Object.fromEntries(NULLABLE_FIELDS.map((k) => [k, form[k] || null])),
  ...Object.fromEntries(NUMERIC_FIELDS.map((k) => [k, Number(form[k]) || 0])),
  offer_made: !!form.offer_made,
});

const ContactIdentityFields = ({ form, set }) => (
  <>
    <Field label="Lead Name *"><Input value={form.lead_name || ''} onChange={(e) => set('lead_name', e.target.value)} data-testid="lead-name-input" /></Field>
    <Field label="Company Name *"><Input value={form.company_name || ''} onChange={(e) => set('company_name', e.target.value)} data-testid="lead-company-input" /></Field>
    <div className="grid grid-cols-2 gap-3">
      <Field label="Email"><Input type="email" value={form.email || ''} onChange={(e) => set('email', e.target.value)} data-testid="lead-email-input" /></Field>
      <Field label="Phone *"><Input value={form.phone || ''} onChange={(e) => set('phone', e.target.value)} data-testid="lead-phone-input" /></Field>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <Field label="Category">
        <Input value={form.category || ''} onChange={(e) => set('category', e.target.value)} placeholder="e.g. Fashion" list="lead-category-suggest" data-testid="lead-category-input" />
      </Field>
      <Field label="City">
        <Input value={form.city || ''} onChange={(e) => set('city', e.target.value)} placeholder="e.g. Mumbai" list="lead-city-suggest" data-testid="lead-city-input" />
      </Field>
    </div>
  </>
);

const ContactRoutingFields = ({ form, set, opts }) => (
  <>
    <SelectField label="Lead Source" value={form.source} onChange={(v) => set('source', v)} options={SOURCES} placeholder="Select source" testId="lead-source-select" />
    <Field label="Next Follow-up Date">
      <Input type="date" value={(form.next_followup_date || '').slice(0, 10)} onChange={(e) => set('next_followup_date', e.target.value)} data-testid="lead-followup-input" />
    </Field>
    <div className="grid grid-cols-2 gap-3">
      <SelectField label="Setter" value={form.setter_id} onChange={(v) => set('setter_id', v)} options={opts.setters} placeholder="Setter" testId="lead-setter-select" />
      <SelectField label="Closer" value={form.closer_id} onChange={(v) => set('closer_id', v)} options={opts.closers} placeholder="Closer" testId="lead-closer-select" />
    </div>
    <div className="grid grid-cols-2 gap-3">
      <SelectField label="Assigned Team" value={form.team} onChange={(v) => set('team', v)} options={opts.teams} placeholder="Team" testId="lead-team-select" />
      <SelectField label="Assigned Role" value={form.assigned_role} onChange={(v) => set('assigned_role', v)} options={ROLE_OPTIONS} placeholder="Role" testId="lead-role-select" />
    </div>
  </>
);

const ContactTab = ({ form, set, members, onComm }) => {
  // Memoised: these lists only change when the member directory changes
  const opts = useMemo(() => {
    const memberOpts = (list) => list.map((m) => ({ value: m.id, label: m.name }));
    return {
      setters: memberOpts(members.filter((m) => m.is_setter || m.role === 'Sales Intern')),
      closers: memberOpts(members.filter((m) => m.is_closer || m.role !== 'Sales Intern')),
      teams: [...new Set(members.flatMap((m) => m.teams || []))],
    };
  }, [members]);
  return (
    <TabsContent value="contact" className="space-y-3 mt-4">
      <ContactIdentityFields form={form} set={set} />
      <div className="flex items-center justify-between rounded-lg border bg-muted/40 px-3 py-2" data-testid="drawer-comm-bar">
        <span className="text-xs text-muted-foreground">Reach out directly — logs the touch automatically</span>
        <CommActions lead={form} onLogged={onComm} />
      </div>
      <ContactRoutingFields form={form} set={set} opts={opts} />
    </TabsContent>
  );
};

const SALE_TYPES = ['1-Call Sale', 'Follow-Up Sale'];

const TimelineTab = ({ form, set }) => (
  <TabsContent value="timeline" className="space-y-3 mt-4">
    <div className="grid grid-cols-2 gap-3">
      <Field label="Date Created"><Input type="date" value={toDateInput(form.date_created)} onChange={(e) => set('date_created', e.target.value ? e.target.value + 'T09:00:00' : null)} data-testid="lead-created-input" /></Field>
      <Field label="First Contact"><Input type="datetime-local" value={toLocalInput(form.first_contact_at)} onChange={(e) => set('first_contact_at', e.target.value || null)} data-testid="lead-first-contact-input" /></Field>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <Field label="Meeting Booked On"><Input type="datetime-local" value={toLocalInput(form.date_meeting_booked)} onChange={(e) => set('date_meeting_booked', e.target.value || null)} data-testid="lead-meeting-booked-input" /></Field>
      <Field label="Date of Meeting"><Input type="datetime-local" value={toLocalInput(form.date_of_meeting)} onChange={(e) => set('date_of_meeting', e.target.value || null)} data-testid="lead-meeting-date-input" /></Field>
    </div>
    <SelectField label="Meeting Status" value={form.meeting_status} onChange={(v) => set('meeting_status', v)} options={MEETING_STATUSES} placeholder="Select outcome" testId="lead-meeting-status-select" />
    <div className="flex items-center justify-between rounded-lg border p-3">
      <div>
        <div className="text-sm font-medium">Offer Made</div>
        <div className="text-xs text-muted-foreground">Was an offer presented on the call?</div>
      </div>
      <Switch checked={!!form.offer_made} onCheckedChange={(v) => set('offer_made', v)} data-testid="lead-offer-switch" />
    </div>
    <SelectField label="Sale Type" value={form.sale_type} onChange={(v) => set('sale_type', v)} options={SALE_TYPES} placeholder="1-Call / Follow-Up" testId="lead-sale-type-select" />
    {form.status === 'Lost' && (
      <Field label="Lost Deal Reason (required)">
        <Select value={form.lost_reason || NONE} onValueChange={(v) => set('lost_reason', v === NONE ? null : v)}>
          <SelectTrigger data-testid="lead-lost-reason-select" className={!form.lost_reason ? 'border-red-400' : ''}>
            <SelectValue placeholder="Why was it lost?" />
          </SelectTrigger>
          <SelectContent>
            {LOST_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
    )}
    <div className="text-xs text-muted-foreground">Last touch: {fmtDateTime(form.last_touch)} (auto-updated on save, call or note)</div>
  </TabsContent>
);

const num = (v) => Number(v) || 0;
const calcEarnings = (form) => Math.max(0, num(form.cash_collected) - num(form.refund_amount)) * num(form.commission_pct) / 100;

const NumField = ({ label, k, form, set, testId, ...rest }) => (
  <Field label={label}><Input type="number" value={form[k] ?? ''} onChange={(e) => set(k, e.target.value)} data-testid={testId} {...rest} /></Field>
);

const DateAtNoonField = ({ label, k, form, set, testId }) => (
  <Field label={label}><Input type="date" value={toDateInput(form[k])} onChange={(e) => set(k, e.target.value ? e.target.value + 'T12:00:00' : null)} data-testid={testId} /></Field>
);

const MoneyTab = ({ form, set }) => (
  <TabsContent value="money" className="space-y-3 mt-4">
    <div className="grid grid-cols-2 gap-3">
      <NumField label="Deposit Amount (₹)" k="deposit_amount" form={form} set={set} testId="lead-deposit-input" />
      <DateAtNoonField label="Deposit Date" k="deposit_date" form={form} set={set} testId="lead-deposit-date-input" />
    </div>
    <div className="grid grid-cols-2 gap-3">
      <NumField label="Total Deal Value (₹)" k="total_deal_value" form={form} set={set} testId="lead-deal-value-input" />
      <NumField label="Cash Collected (₹)" k="cash_collected" form={form} set={set} testId="lead-cash-input" />
    </div>
    <div className="grid grid-cols-2 gap-3">
      <DateAtNoonField label="Date Paid In Full" k="date_paid_in_full" form={form} set={set} testId="lead-pif-input" />
      <NumField label="Refund / Clawback (₹)" k="refund_amount" form={form} set={set} testId="lead-refund-input" />
    </div>
    <NumField label="Commission % (rep)" k="commission_pct" form={form} set={set} testId="lead-commission-input" step="0.1" />
    <div className="rounded-lg border p-3 space-y-3">
      <div className="text-xs font-semibold">Retainer Contract <span className="font-normal text-muted-foreground">— drives the Retention Pool</span></div>
      <div className="grid grid-cols-2 gap-3">
        <NumField label="Total Contract Value (₹)" k="total_contract_value" form={form} set={set} testId="lead-contract-value-input" />
        <NumField label="Contract Period (Months)" k="contract_period_months" form={form} set={set} testId="lead-contract-period-input" min="0" />
      </div>
      <NumField label="Monthly Retainer (₹)" k="monthly_retainer" form={form} set={set} testId="lead-monthly-retainer-input" />
      <p className="text-[11px] text-muted-foreground">Once Won, the Retention Pool accrues a % of the Monthly Retainer each month of the contract. It becomes Ready to Disburse when the period ends — cancelling the deal freezes it for Admin review.</p>
    </div>
    <div className="rounded-lg bg-secondary/60 border border-secondary p-3 flex items-center justify-between">
      <div className="text-sm font-medium text-secondary-foreground">Earnings (auto)</div>
      <div className="font-heading font-bold text-primary" data-testid="lead-earnings-text">{inr(calcEarnings(form))}</div>
    </div>
    <p className="text-[11px] text-muted-foreground">Earnings = (Cash Collected − Refunds) × Commission %. Won deals auto-sync to the Incentive engine — nothing entered twice.</p>
  </TabsContent>
);

const NotesTab = ({ form, set, notes, newNote, setNewNote, addNote }) => (
  <TabsContent value="notes" className="space-y-3 mt-4">
    <Field label="Remarks">
      <Textarea rows={2} value={form.remarks || ''} onChange={(e) => set('remarks', e.target.value)} placeholder="Free-text remarks about this lead" data-testid="lead-remarks-input" />
    </Field>
    <div className="flex gap-2">
      <Input value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Add a timestamped note…" data-testid="lead-note-input" onKeyDown={(e) => e.key === 'Enter' && addNote()} />
      <Button onClick={addNote} variant="secondary" data-testid="lead-note-add-button">Add</Button>
    </div>
    <div className="space-y-2">
      {notes.map((n) => (
        <div key={n.id} className="rounded-lg border p-2.5 text-sm" data-testid={`lead-note-${n.id}`}>
          <div>{n.text}</div>
          <div className="text-[11px] text-muted-foreground mt-1">{n.author_name} · {fmtDateTime(n.created_at)}</div>
        </div>
      ))}
      {notes.length === 0 && <div className="text-xs text-muted-foreground text-center py-4">No notes yet</div>}
    </div>
  </TabsContent>
);

// ---------- Vertical Activity Timeline (status changes + notes, all IST) ----------

const TIMELINE_DOT = {
  created: 'bg-emerald-500',
  status_changed: 'bg-primary',
  note: 'bg-violet-500',
  reassigned: 'bg-sky-500',
  archived: 'bg-red-400',
  quotation_reminder_sent: 'bg-amber-500',
};

const timelineEvents = (activity, notes) => {
  const acts = activity.map((a) => ({ ...a, _type: a.action || 'updated', _ts: a.created_at }));
  const nts = notes.map((n) => ({ id: `note-${n.id}`, _type: 'note', _ts: n.created_at,
    user_name: n.author_name, text: n.text }));
  return [...acts, ...nts].sort((a, b) => String(b._ts).localeCompare(String(a._ts)));
};

const TimelineEntry = ({ ev, isLast }) => (
  <div className="relative pl-6 pb-4" data-testid={`timeline-item-${ev.id}`}>
    {!isLast && <span className="absolute left-[5px] top-4 bottom-0 w-px bg-border" aria-hidden="true" />}
    <span className={`absolute left-0 top-1.5 h-[11px] w-[11px] rounded-full ring-4 ring-background ${TIMELINE_DOT[ev._type] || 'bg-slate-400'}`} aria-hidden="true" />
    <div className="flex items-center justify-between gap-2">
      <span className="text-xs font-semibold capitalize">{ev._type === 'note' ? 'Note added' : String(ev._type).replace(/_/g, ' ')}</span>
      <span className="text-[10px] text-muted-foreground whitespace-nowrap">{fmtDateTime(ev._ts)}</span>
    </div>
    <div className="text-[11px] text-muted-foreground">by {ev.user_name || 'System'}</div>
    {ev._type === 'note' ? (
      <div className="mt-1 rounded-lg border bg-violet-50/50 border-violet-200/60 px-2.5 py-1.5 text-xs">{ev.text}</div>
    ) : (
      (ev.changes || []).map((c, i) => (
        <div key={`${c.field}-${i}`} className="text-xs py-0.5">
          <span className="text-muted-foreground">{c.label}:</span>{' '}
          <span className="line-through text-red-500/70">{c.old}</span>{' '}
          <span className="text-muted-foreground">→</span>{' '}
          <span className="font-medium text-emerald-700">{c.new}</span>
        </div>
      ))
    )}
  </div>
);

const ActivityTab = ({ activity, notes }) => {
  const events = timelineEvents(activity, notes);
  return (
    <TabsContent value="activity" className="mt-4">
      <div className="text-[11px] text-muted-foreground mb-3">Chronological timeline — every status change and note, in IST</div>
      <div data-testid="lead-activity-list">
        {events.map((ev, i) => (
          <TimelineEntry key={ev.id} ev={ev} isLast={i === events.length - 1} />
        ))}
        {events.length === 0 && <div className="text-xs text-muted-foreground text-center py-6">No activity recorded yet</div>}
      </div>
    </TabsContent>
  );
};

const DrawerActionBar = ({ save, saving, markCalled, del, canDelete }) => (
  <div className="fixed bottom-0 right-0 w-full sm:max-w-lg bg-card border-t px-4 py-3 flex items-center gap-2 z-20">
    <Button onClick={save} disabled={saving} className="flex-1" data-testid="lead-drawer-save-button">
      {saving ? <Loader2 className="animate-spin mr-1" size={15} /> : <Save size={15} className="mr-1" />} Save
    </Button>
    <Button variant="outline" onClick={markCalled} data-testid="lead-drawer-mark-called-button">
      <PhoneCall size={15} className="mr-1" /> Called
    </Button>
    {canDelete && (
      <Button variant="ghost" size="icon" onClick={del} className="text-destructive" data-testid="lead-drawer-delete-button" aria-label="Move lead to archive">
        <Trash2 size={15} />
      </Button>
    )}
  </div>
);

/** Loads/reset drawer data (form copy, notes, activity) whenever the lead changes. */
const useLeadDrawerData = (lead) => {
  const [form, setForm] = useState(null);
  const [notes, setNotes] = useState([]);
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    if (lead) {
      setForm({ ...lead });
      api.get(`/leads/${lead.id}/notes`).then((r) => setNotes(r.data)).catch((e) => logger.error('Failed to load lead notes', e));
      api.get(`/leads/${lead.id}/activity`).then((r) => setActivity(r.data)).catch((e) => logger.error('Failed to load lead activity', e));
    } else {
      setForm(null);
      setNotes([]);
      setActivity([]);
    }
  }, [lead]);

  return { form, setForm, notes, setNotes, activity };
};

/** Returns a user-facing error string, or null when the lead form is valid to save. */
const validateLeadForm = (form) => {
  const missing = [['lead_name', 'Lead Name'], ['company_name', 'Company Name'], ['phone', 'Mobile Number']]
    .filter(([k]) => !(form[k] || '').trim()).map(([, label]) => label);
  if (missing.length) return `Required: ${missing.join(', ')}`;
  if (form.status === 'Lost' && !form.lost_reason) return 'Lost reason is required for Lost leads';
  return null;
};

/** Quick-notes state + add handler, split out of the main drawer hook. */
const useLeadNotes = (form, setNotes) => {
  const [newNote, setNewNote] = useState('');

  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      const { data } = await api.post(`/leads/${form.id}/notes`, { text: newNote.trim() });
      setNotes((n) => [data, ...n]);
      setNewNote('');
      toast.success('Note added');
    } catch { toast.error('Failed to add note'); }
  };

  return { newNote, setNewNote, addNote };
};

/** Drawer handlers (save, touch, notes, archive) composed over the data hook. */
const useLeadDrawer = ({ lead, onClose, onSaved, onDeleted }) => {
  const { form, setForm, notes, setNotes, activity } = useLeadDrawerData(lead);
  const { newNote, setNewNote, addNote } = useLeadNotes(form, setNotes);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = useCallback(async () => {
    if (!form) return;
    const err = validateLeadForm(form);
    if (err) {
      toast.error(err);
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put(`/leads/${form.id}`, buildLeadPayload(form));
      onSaved(data);
      emitLeadChanged(data);
      toast.success('Lead saved');
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [form, onSaved, onClose]);

  const markCalled = async () => {
    try {
      const { data } = await api.post(`/leads/${form.id}/touch`, { touch_type: 'call' });
      setForm((f) => ({ ...f, ...data }));
      onSaved({ ...form, ...data });
      emitLeadChanged(data);
      toast.success('Marked as called — last touch updated');
    } catch { toast.error('Failed'); }
  };

  const del = async () => {
    if (!window.confirm('Move this lead to the Archive? It stays restorable by an Admin for 30 days.')) return;
    try {
      await api.delete(`/leads/${form.id}`);
      toast.success('Lead moved to Archive — restorable for 30 days');
      onDeleted?.(form.id);
      emitLeadChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Archive failed'); }
  };

  const onComm = (ld) => {
    setForm((f) => ({ ...f, ...ld }));
    onSaved?.(ld);
    emitLeadChanged(ld);
  };

  return { form, set, notes, activity, newNote, setNewNote, saving, save, markCalled, addNote, del, onComm };
};

const DrawerHeader = ({ form, set }) => (
  <SheetHeader className="px-4 pt-4 pb-2 sticky top-0 bg-card z-10 border-b">
    <div className="flex items-center justify-between gap-2">
      <SheetTitle className="font-heading truncate">{form.lead_name}</SheetTitle>
      <div className="flex items-center gap-1.5 mr-6">
        {form.aging && <Badge className="bg-orange-100 text-orange-700 border-orange-300" variant="outline"><Flame size={11} className="mr-0.5" />Aging</Badge>}
        <Select value={form.status} onValueChange={(v) => set('status', v)}>
          <SelectTrigger className="h-8 w-[150px] text-xs" data-testid="lead-drawer-status-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </div>
  </SheetHeader>
);

export const LeadDrawer = ({ lead, members, open, onClose, onSaved, onDeleted }) => {
  const { hasPerm } = useAuth();
  const d = useLeadDrawer({ lead, onClose, onSaved, onDeleted });
  const [suggest, setSuggest] = useState({ categories: [], cities: [] });

  // Auto-suggest Category/City from existing values (loaded once per drawer open)
  useEffect(() => {
    if (!open || suggest.categories.length || suggest.cities.length) return;
    api.get('/leads-meta')
      .then(({ data }) => setSuggest({ categories: data.categories || [], cities: data.cities || [] }))
      .catch(() => {});
  }, [open, suggest]);

  if (!d.form) return null;

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto p-0" data-testid="lead-drawer">
        <DrawerHeader form={d.form} set={d.set} />
        <datalist id="lead-category-suggest">
          {suggest.categories.map((c) => <option key={c} value={c} />)}
        </datalist>
        <datalist id="lead-city-suggest">
          {suggest.cities.map((c) => <option key={c} value={c} />)}
        </datalist>

        <div className="px-4 pb-24">
          <Tabs defaultValue="contact" className="mt-3">
            <TabsList className="grid grid-cols-6 w-full">
              <TabsTrigger value="contact" data-testid="lead-tab-contact">Contact</TabsTrigger>
              <TabsTrigger value="timeline" data-testid="lead-tab-timeline">Timeline</TabsTrigger>
              <TabsTrigger value="money" data-testid="lead-tab-money">Money</TabsTrigger>
              <TabsTrigger value="meetings" data-testid="lead-tab-meetings">Meetings</TabsTrigger>
              <TabsTrigger value="notes" data-testid="lead-tab-notes">Notes</TabsTrigger>
              <TabsTrigger value="activity" data-testid="lead-tab-activity">Activity</TabsTrigger>
            </TabsList>
            <ContactTab form={d.form} set={d.set} members={members} onComm={d.onComm} />
            <TimelineTab form={d.form} set={d.set} />
            <MoneyTab form={d.form} set={d.set} />
            <TabsContent value="meetings" className="mt-4">
              {hasPerm('meetings', 'view') ? (
                <LeadMeetingsTab lead={d.form} canCreate={hasPerm('meetings', 'create')} />
              ) : (
                <div className="text-xs text-muted-foreground text-center py-6">You don't have access to the Meetings module</div>
              )}
            </TabsContent>
            <NotesTab form={d.form} set={d.set} notes={d.notes} newNote={d.newNote} setNewNote={d.setNewNote} addNote={d.addNote} />
            <ActivityTab activity={d.activity} notes={d.notes} />
          </Tabs>
        </div>

        <DrawerActionBar save={d.save} saving={d.saving} markCalled={d.markCalled} del={d.del} canDelete={hasPerm('leads', 'delete')} />
      </SheetContent>
    </Sheet>
  );
};
